import React, { useMemo, useState } from 'react';
import { Idea, Topic } from '../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { startOfDay, endOfDay, isAfter, isBefore, parseISO, subDays, format, differenceInDays, isValid } from 'date-fns';
import { Calendar as CalendarIcon, ArrowRight, RefreshCw } from 'lucide-react';

interface DashboardProps {
  ideas: Idea[];
  topics: Topic[];
  onRefresh: () => void;
  isRefreshing: boolean;
}

export const Dashboard: React.FC<DashboardProps> = ({ ideas, topics, onRefresh, isRefreshing }) => {
  // Default to last 30 days
  const [startDate, setStartDate] = useState<string>(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [compare, setCompare] = useState(false);

  // Filter ideas based on date range safely
  const filterByDate = (list: Idea[], start: Date, end: Date) => {
    return list.filter(idea => {
      if (!idea.createdAt) return false;
      try {
        const date = parseISO(idea.createdAt);
        if (!isValid(date)) return false;
        return isAfter(date, start) && isBefore(date, end);
      } catch (e) {
        return false;
      }
    });
  };

  const currentStats = useMemo(() => {
    const start = startOfDay(parseISO(startDate));
    const end = endOfDay(parseISO(endDate));
    const filtered = filterByDate(ideas, start, end);
    return {
      total: filtered.length,
      done: filtered.filter(i => i.isDone).length,
      pending: filtered.filter(i => !i.isDone).length,
      filteredIdeas: filtered
    };
  }, [ideas, startDate, endDate]);

  const previousStats = useMemo(() => {
    if (!compare) return null;
    
    const startCurrent = parseISO(startDate);
    const endCurrent = parseISO(endDate);
    const duration = differenceInDays(endCurrent, startCurrent) + 1;
    
    // Previous period is strictly before the start date
    const endPrev = subDays(startCurrent, 1);
    const startPrev = subDays(endPrev, duration - 1); 

    const filtered = filterByDate(ideas, startOfDay(startPrev), endOfDay(endPrev));

    return {
      total: filtered.length,
      done: filtered.filter(i => i.isDone).length,
      pending: filtered.filter(i => !i.isDone).length,
      label: `${format(startPrev, 'MMM dd')} - ${format(endPrev, 'MMM dd')}`
    };
  }, [ideas, startDate, endDate, compare]);

  const topicData = useMemo(() => {
    return topics.map(topic => {
      const topicIdeas = currentStats.filteredIdeas.filter(i => i.topicId === topic.id);
      return {
        name: topic.name,
        Total: topicIdeas.length,
        Completed: topicIdeas.filter(i => i.isDone).length,
      };
    }).filter(d => d.Total > 0);
  }, [topics, currentStats]);

  const statusData = [
    { name: 'Completed', value: currentStats.done },
    { name: 'Pending', value: currentStats.pending },
  ];
  
  const COLORS = ['#10b981', '#ef4444'];

  const getDiff = (current: number, prev: number) => {
    if (prev === 0) return current > 0 ? '+100%' : '0%';
    const diff = ((current - prev) / prev) * 100;
    return `${diff > 0 ? '+' : ''}${diff.toFixed(0)}%`;
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center gap-4">
            <div>
               <h2 className="text-xl font-bold text-gray-800">Content Performance</h2>
               <p className="text-xs text-gray-500">Analyze your output and progress</p>
            </div>
             <button 
                onClick={onRefresh} 
                disabled={isRefreshing}
                className="p-1.5 bg-gray-50 text-gray-400 hover:text-primary rounded-full hover:bg-gray-100 transition-all disabled:opacity-50"
                title="Reload Data"
            >
                <RefreshCw size={16} className={`${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <div className="flex items-center space-x-2 bg-gray-50 p-2 rounded-lg border border-gray-200 w-full sm:w-auto">
            <CalendarIcon size={16} className="text-gray-400" />
            <input 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
                className="bg-transparent text-sm outline-none text-gray-600 w-28"
            />
            <ArrowRight size={14} className="text-gray-300" />
            <input 
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)} 
                className="bg-transparent text-sm outline-none text-gray-600 w-28"
            />
          </div>

          <label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer select-none">
            <input 
                type="checkbox" 
                checked={compare} 
                onChange={(e) => setCompare(e.target.checked)}
                className="rounded text-primary focus:ring-primary" 
            />
            <span>Compare prev. period</span>
          </label>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
            { label: 'Total Ideas', value: currentStats.total, prev: previousStats?.total },
            { label: 'Completed', value: currentStats.done, prev: previousStats?.done, color: 'text-green-600' },
            { label: 'Pending', value: currentStats.pending, prev: previousStats?.pending, color: 'text-primary' },
        ].map((stat, i) => (
             <div key={i} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">{stat.label}</p>
                <div className="flex items-end justify-between mt-2">
                    <p className={`text-3xl font-bold ${stat.color || 'text-secondary'}`}>{stat.value}</p>
                    {compare && previousStats && (
                        <div className="text-right">
                            <span className={`text-xs font-bold ${stat.value >= (stat.prev || 0) ? 'text-green-500' : 'text-red-500'}`}>
                                {getDiff(stat.value, stat.prev || 0)}
                            </span>
                            <p className="text-[10px] text-gray-400">vs {stat.prev}</p>
                        </div>
                    )}
                </div>
            </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 h-80">
          <h3 className="text-sm font-bold text-gray-700 mb-4">Topic Breakdown</h3>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topicData} margin={{ top: 5, right: 30, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
              <XAxis dataKey="name" tick={{fontSize: 10}} stroke="#9ca3af" interval={0} />
              <YAxis tick={{fontSize: 10}} stroke="#9ca3af" />
              <Tooltip cursor={{fill: '#f9fafb'}} contentStyle={{fontSize:'12px', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
              <Legend wrapperStyle={{fontSize: '12px'}} />
              <Bar dataKey="Total" fill="#1e3a8a" radius={[4, 4, 0, 0]} barSize={20} />
              <Bar dataKey="Completed" fill="#10b981" radius={[4, 4, 0, 0]} barSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 h-80">
          <h3 className="text-sm font-bold text-gray-700 mb-4">Completion Status</h3>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" height={36} wrapperStyle={{fontSize: '12px'}}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};